/* ============================================================================
   Estelle cart drawer  ::  estelle-cart-drawer.js

   Only four things need JS. Quantity, remove, re-render and drawer open/close
   are already handled by the theme's own CartSection, which re-fetches
   ?section_id=mini-cart and swaps .Cart after every change. So all we do is
   change the cart, then fire the theme's own 'cart:refresh' event and let it
   redraw everything from Liquid.

     1. upsell "+ Add"      -> POST /cart/add.js
     2. warranty switch     -> POST /cart/add.js or /cart/change.js (qty 0)
     3. upsell arrows       -> scroll the list by one card
     4. upsell products     -> Shopify recommendations, rendered by
                               sections/estelle-cart-upsell.liquid and swapped
                               over the server-rendered fallback list

   Delegated from document, so it survives every re-render. No re-init needed.
   ========================================================================= */
(function () {
  'use strict';

  var DRAWER = '#sidebar-cart';

  // The theme listens for cart:refresh with a delegate rooted at
  // document.documentElement, so the event has to be fired THERE. Dispatching
  // on `document` bubbles the wrong way and is silently ignored.
  function refresh() {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) { return r.json().then(function (e) { throw new Error(e.description || r.statusText); }); }
      return r.json();
    });
  }

  function busy(el, on) {
    el.classList.toggle('is-busy', on);
    if (on) { el.setAttribute('aria-busy', 'true'); } else { el.removeAttribute('aria-busy'); }
  }

  document.addEventListener('click', function (event) {
    var drawer = event.target.closest ? event.target.closest(DRAWER) : null;
    if (!drawer) { return; }

    /* ---------------------------------------------------------- upsell add */
    var add = event.target.closest('[data-ecd-add]');
    if (add) {
      event.preventDefault();
      if (add.classList.contains('is-busy')) { return; }
      busy(add, true);
      // a card for a product with sizes/colours carries a picker; honour it
      var picker = add.parentElement && add.parentElement.querySelector('[data-ecd-variant]');
      var variantId = picker && picker.value ? picker.value : add.getAttribute('data-ecd-add');
      post('/cart/add.js', { items: [{ id: Number(variantId), quantity: 1 }] })
        .then(refresh)
        .catch(function (e) { busy(add, false); console.warn('[estelle-cart] add failed:', e.message); });
      return;
    }

    /* ---------------------------------------------------- warranty toggle  */
    var sw = event.target.closest('[data-ecd-warranty]');
    if (sw) {
      event.preventDefault();
      if (sw.classList.contains('is-busy')) { return; }
      var id = Number(sw.getAttribute('data-ecd-warranty'));
      var on = sw.getAttribute('aria-checked') === 'true';
      busy(sw, true);
      sw.setAttribute('aria-checked', on ? 'false' : 'true');
      var call = on
        ? post('/cart/change.js', { id: String(id), quantity: 0 })
        : post('/cart/add.js', { items: [{ id: id, quantity: 1 }] });
      call.then(refresh).catch(function (e) {
        busy(sw, false);
        sw.setAttribute('aria-checked', on ? 'true' : 'false');
        console.warn('[estelle-cart] warranty toggle failed:', e.message);
      });
      return;
    }

    /* ------------------------------------------------------- upsell arrows */
    var arrow = event.target.closest('[data-ecd-scroll]');
    if (arrow) {
      event.preventDefault();
      var list = drawer.querySelector('[data-ecd-list]');
      if (!list) { return; }
      var card = list.firstElementChild;
      // ponytail: one card + the 8px gap; no per-card measurement loop needed
      var step = card ? card.offsetHeight + 8 : 101;
      list.scrollBy({ top: step * Number(arrow.getAttribute('data-ecd-scroll')), behavior: 'smooth' });
    }
  });

  /* ------------------------------------------------- Shopify recommendations --
     The drawer renders a collection-based list server-side so there is never an
     empty panel or a layout jump. As soon as the page (or a re-render) settles we
     ask Shopify for real recommendations against the last product added and swap
     them in. If that returns nothing, the fallback list simply stays. */
  function loadRecommendations(drawer) {
    var list = drawer.querySelector('[data-ecd-list]');
    if (!list) { return; }
    var seed = list.getAttribute('data-ecd-seed');
    if (!seed || list.getAttribute('data-ecd-done') === seed) { return; }
    list.setAttribute('data-ecd-done', seed); // claim it now so we ask only once

    fetch('/recommendations/products?section_id=estelle-cart-upsell&limit=10&product_id=' + encodeURIComponent(seed),
      { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : Promise.reject(new Error('HTTP ' + r.status)); })
      .then(function (html) {
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var cards = tmp.querySelectorAll('.ecd-up__card');
        if (!cards.length) { return; }
        list.innerHTML = '';
        for (var i = 0; i < cards.length; i++) { list.appendChild(cards[i]); }
        syncArrows(list);
      })
      .catch(function (e) { console.warn('[estelle-cart] recommendations failed:', e.message); });
  }

  /* Keep the arrows honest: dim them at the ends of the list. */
  function syncArrows(list) {
    var wrap = list.closest('.ecd-up');
    if (!wrap) { return; }
    var up = wrap.querySelector('[data-ecd-scroll="-1"]');
    var down = wrap.querySelector('[data-ecd-scroll="1"]');
    var atTop = list.scrollTop <= 1;
    var atEnd = list.scrollTop + list.clientHeight >= list.scrollHeight - 1;
    if (up) { up.disabled = atTop; }
    if (down) { down.disabled = atEnd; }
  }

  document.addEventListener('scroll', function (e) {
    if (e.target && e.target.hasAttribute && e.target.hasAttribute('data-ecd-list')) { syncArrows(e.target); }
  }, true);

  // .Cart is replaced wholesale on every cart change, so re-sync then too.
  function watch() {
    var el = document.querySelector(DRAWER);
    if (!el) { return; }
    var sync = function () {
      var list = el.querySelector('[data-ecd-list]');
      if (list) { syncArrows(list); }
      loadRecommendations(el);
    };
    new MutationObserver(sync).observe(el, { childList: true });
    sync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }
}());
